import React from "react";
import PropTypes from 'prop-types';
import Heading from "../Heading/Heading";

interface CollapsiblePanelProps extends React.HTMLAttributes<HTMLElement> {
    label?: string;
    isShaded?: boolean;
    // Baseline arguments
    children?: React.ReactNode;
    className?: string;
}

interface CollapsiblePanelState {
    isExpanded: boolean;
}

/**
 * A panel that shows text while collapsed, but can be clicked on to expand and show other components.
 * 
 * ## _
 * ### Arguments
 * @param {string} label - The text to be shown when the panel is collapsed.
 * @param {boolean} isShaded - A flag that makes the toggleable area more shaded.
 * @param {React.ReactNode} children - The contents inside the component. Currently, this does nothing, but exists for parity.
 * @param {string} className - CSS classes to apply to the component.
 */
class CollapsiblePanel extends React.Component<CollapsiblePanelProps, CollapsiblePanelState> {
    constructor(props: CollapsiblePanelProps) {
        super(props);
        this.state = {
            isExpanded: false,
        };
    }

    static propTypes = {
        label: PropTypes.string,
        isShaded: PropTypes.bool,
        children: PropTypes.node,
        className: PropTypes.string,
    }

    static defaultProps = {
        className: "",
    }

    handlePanelToggle = () => {
        this.setState((prevState) => ({
            isExpanded: !prevState.isExpanded,
        }));
    }
 
    render() {
        const { label, isShaded, children, className, ...rest } = this.props;

        let classes = `m-collapsible-panel`

        if (this.state.isExpanded) classes += ` m-collapsible-panel--expanded`;

        if (className) classes += ` ${className}`;

        return (
            <div className={classes}>
                <a className="m-collapsible-panel__heading" aria-expanded={this.state.isExpanded}>
                    <Heading size={5} className="m-collapsible-panel__heading-label">{label}</Heading>
                    <span className="m-collapsible-panel__collapse-toggle" onClick={this.handlePanelToggle}>
                        <svg className="m-collapsible-panel__collapse-icon" xmlns="http://www.w3.org/2000/svg" width="15.08" height="15.08" viewBox="0 0 15.08 15.08" aria-hidden={!this.state.isExpanded}>
                            <g id="Layer_2" data-name="Layer 2" transform="translate(-6.46 -6.46)">
                                <g id="Layer_1" data-name="Layer 1">
                                    <rect className="m-collapsible-panel__collapse-icon-horizontal" id="Rectangle_45" data-name="Rectangle 45" width="15.08" height="2.15" transform="translate(6.46 12.92)"></rect>
                                    <rect className="m-collapsible-panel__collapse-icon-vertical" id="Rectangle_46" data-name="Rectangle 46" width="2.15" height="15.08" transform="translate(12.92 6.46)"></rect>
                                </g>
                            </g>
                        </svg>
                    </span>
                </a>
                <div className="m-collapsible-panel__contents">
                    {children}
                </div>
            </div>
        );
    }
}

export default CollapsiblePanel;